import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { config, Font } from '../../constants'
import { IFlowCore } from '../../modules/epflow'
import { Locale } from '../../modules/locale'
import MainModule from '../../modules/main'
import { Routing } from '../../modules/routing'

Locale.getInstance().registerKeySet('fr', 'lvc-footer-navigation', {
  quit: 'Quitter',
  return: 'Retour',
  next: 'Suivant',
  commit: 'Soumettre',
  saved: 'Dernière Sauvegarde',
  notSaved: 'Non sauvegardé',
  footerNavButtons: {
    confirmation: 'Confirmation',
    description: 'Description',
    details: 'Détails',
    image: 'Image',
    contributors: 'Contributeurs',
    representations: 'Représentations',
    validation: 'Validation',
    selectContributor: 'Contributeur',
    selectContributionType: 'Type',
    addEvents: 'Événements',
    recap: 'Résumé',
  },
})

Locale.getInstance().registerKeySet('en', 'lvc-footer-navigation', {
  quit: 'Quit',
  return: 'Back',
  next: 'Next',
  commit: 'Submit',
  saved: 'Last saved',
  notSaved: 'Not Saved',
  footerNavButtons: {
    confirmation: 'Confirmation',
    description: 'Description',
    details: 'Details',
    image: 'Image',
    contributors: 'Contributors',
    representations: 'Representations',
    validation: 'Validation',
    selectContributor: 'Contributor',
    selectContributionType: 'Type',
    addEvents: 'Events',
    recap: 'Summary',
  },
})

export interface LvcFooterNavigation {
  flowGroup: string
  progress: string
  progressPercentage: number
  title: string
  placeholder: string
  open: boolean
  onChange: string
  defaultValue: any
  value: string
  step: string
  stepInfo: any
  data: { [key: string]: any }
  steps: { key: string; label: string }[]
}

export class LvcFooterNavigation extends MinzeElement {
  private events: any[] = []
  private selectedIds: string[] = []
  private filter: string = ''
  private core: IFlowCore | undefined

  exhibitionSteps = [
    {
      key: 'createExhibitionDescription',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.description'),
    },
    {
      key: 'createExhibitionDetails',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.details'),
    },
    {
      key: 'createExhibitionAddImage',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.image'),
    },
    {
      key: 'addExhibitionContributors',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.contributors'),
    },
    {
      key: 'eventFirstRecap',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.validation'),
    },
  ]

  festivalSteps = [
    {
      key: 'createEventDescription',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.description'),
    },
    {
      key: 'createEventDetails',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.details'),
    },
    {
      key: 'createEventAddImage',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.image'),
    },
    {
      key: 'addEventContributors',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.contributors'),
    },
    {
      key: 'finalEventRecap',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.validation'),
    },
  ]

  existingFestivalSteps = [
    {
      key: 'eventConfirmation',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.confirmation'),
    },
    {
      key: 'eventFirstRecap',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.representations'),
    },
    {
      key: 'finalEventRecap',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.validation'),
    },
  ]

  eventSteps = [
    {
      key: 'createEventDescription',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.description'),
    },
    {
      key: 'createEventAddImage',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.image'),
    },
    {
      key: 'addEventContributors',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.contributors'),
    },
    {
      key: 'eventFirstRecap',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.representations'),
    },
    {
      key: 'finalEventRecap',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.validation'),
    },
  ]

  existingEventSteps = [
    {
      key: 'eventConfirmation',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.confirmation'),
    },
    {
      key: 'eventFirstRecap',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.representations'),
    },
    {
      key: 'finalEventRecap',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.validation'),
    },
  ]

  contributionSteps = [
    {
      key: 'createContributionSearchOrAdd',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.selectContributor'),
    },
    {
      key: 'createContributionSelectType',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.selectContributionType'),
    },
    {
      key: 'createContributionAddEvents',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.addEvents'),
    },
    {
      key: 'createContributionRecap',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.recap'),
    },
  ]

  contributorSteps = [
    {
      key: 'createContributorInfo',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.description'),
    },
    {
      key: 'createContributorAddImage',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.image'),
    },
    {
      key: 'createContributorRecap',
      label: Locale.getKey('lvc-footer-navigation.footerNavButtons.recap'),
    },
  ]

  attrs: Attrs = [
    'title',
    'placeholder',
    ['default-value', ''],
    ['on-change', ''],
    ['step', '1'],
    ['flow-group', ''],
    ['progress', 0],
    ['data-type', 'event'],
  ]

  reactive: Reactive = [
    ['value', ''],
    ['tooltipOpened', false],
    ['stepInfo', null],
    ['data', {}],
    ['loading', false],
    ['saveState', 'unsaved'],
    ['lastSaveTime', ''],
    ['steps', [], true],
  ]

  tooltipElement: HTMLElement | null = null

  static observedAttributes = ['title', 'on-change', 'progress', 'step', 'data-type']

  onAttributeChange(name: string, oldValue: string, newValue: string) {
    // If the "step" attribute is changed and core exists, update navigation steps buttons
    if (name === 'step' && this.core) {
      this.updateNavigationSteps()
    }
    // Always rerender
    this.rerender()
  }

  /* When we load a draft, we get it's last saved time and show it by default in the footer
   */
  onDraftLoaded(event: Event): void {
    const customEvent = event as CustomEvent
    const draftLastSaved = customEvent.detail
    if (draftLastSaved) {
      // Convert the ISO timestamp from Firebase to a localized time string.
      const dateObj = new Date(draftLastSaved)
      this.lastSaveTime = dateObj.toLocaleString('fr-FR')
      // Mark as saved since we're loading an existing draft.
      this.saveState = 'saved'
    }
    this.rerender()
  }

  updateNavigationSteps() {
    if (!this.core) return

    // Get current state from the core
    this.stepInfo = this.core.getCurrentTransitionStep()
    this.data = this.core.getData() || {}
    const isModifying = !!this.data?.control_modifyingExistingEvent

    // Pick steps based on the data type
    switch (this.dataType) {
      case 'festival':
        this.steps = isModifying ? this.existingFestivalSteps : this.festivalSteps
        break
      case 'event':
        this.steps = isModifying ? this.existingEventSteps : this.eventSteps
        break
      case 'contribution':
        this.steps = this.contributionSteps
        break
      case 'contributor':
        this.steps = this.contributorSteps
        break
      case 'exhibition':
      default:
        this.steps = this.exhibitionSteps
        break
    }

    // Treat "addRepresentation" as "eventFirstRecap"
    const currentStep =
      this.stepInfo?.stepName === 'addRepresentation' ? 'eventFirstRecap' : this.stepInfo?.stepName

    // Determine the index of the current step and calculate progress percentage
    const index = this.steps.findIndex(step => step.key === currentStep)
    const progressCalculation = Math.round(100 * (index / (this.steps.length - 1)))
    this.progressPercentage = progressCalculation || parseInt(this.progress, 10)
  }

  onFlowcoreReady(): void {
    this.core = MainModule.getInstance().getFlowManager().getCore(this.flowGroup)

    for (let i = 1; i <= 4; i++) {
      const stepElement = this.select(`.step${i}`) as HTMLElement
      if (stepElement) {
        stepElement.style.backgroundColor =
          i <= Number(this.step)
            ? 'var(--Minuit, #1E007D)'
            : 'var(--nuit-30, rgba(30, 0, 125, 0.30)'
      }
    }

    // Update the navigation steps after the core is ready
    this.updateNavigationSteps()
    this.rerender()
  }

  async onClickButtonQuit(): Promise<void> {
    await this?.core?.save(true) // immediately save the data
    // go back to pro space page
    const mainModule = MainModule.getInstance()
    const routing = mainModule.getRouting()
    const lang = mainModule.getLang()
    window.location.href = config.proSpaceDraftsUrl[lang as 'fr' | 'en']
  }

  async onClickButtonReturn() {
    await this?.core?.prev()
  }

  async onClickButtonNext() {
    await this?.core?.next()
  }

  onBlur() {
    this.open = false
    this.rerender()
  }

  onClickButtonCommit(): void {
    // render the confirmation popup
    let attr: { name: string; value: string }[] = []

    if (this.dataType === 'contribution') {
      attr.push({ name: 'preset', value: 'confirmContribution' })
    } else if (this.dataType === 'contributor') {
      attr.push({ name: 'preset', value: 'confirmContributor' })
    } else if (this.dataType === 'event') {
      attr.push({ name: 'preset', value: 'confirmEvent' })
    } else if (this.dataType === 'festival') {
      attr.push({ name: 'preset', value: 'confirmFestival' })
    } else {
      attr.push({ name: 'preset', value: 'confirmExhibition' })
    }
    const mainModule = window.LVC_MAIN_MODULE
    mainModule.injectIntoDOM('lvc-state-confirmation-popup', attr)
  }

  onClickPopupEdit(): void {
    const mainModule = MainModule.getInstance()
    mainModule.hideModal('lvc-state-confirmation-popup')
  }

  // when the user clicks on 'submit' in the confirmation popup
  // it commits the data via the flow core's onCommit callback
  async onClickPopupSubmit(): Promise<void> {
    this.loading = true
    this.core = MainModule.getInstance().getFlowManager().getCore(this.flowGroup)
    const mainModule = MainModule.getInstance()

    // submit the data and if successful, go to the next step
    try {
      await this.core?.commit()
    } catch (error) {
      console.error(error)
    }
    this.loading = false
  }

  onNavigationButtonClick(event: Event): void {
    const button = (event.target as Element)?.closest('.navigation-button') as Element | null
    if (!button) return
    const stepKey = button.getAttribute('data-key')
    if (stepKey) this.core?.goTo(stepKey)
  }

  onSaveStateChange(event: Event): void {
    const customEvent = event as CustomEvent
    const newState = customEvent.detail

    if (newState === 'saved') {
      this.lastSaveTime = new Date().toLocaleTimeString()
      this.saveState = 'saved'

      // Get the element of saved state and do a little animation
      const saveStateEl = this.select('.save-state-text')
      if (saveStateEl) {
        saveStateEl.classList.add('just-saved')
        setTimeout(() => {
          saveStateEl.classList.remove('just-saved')
        }, 1000)
      }
    } else if (newState === 'saving') {
      this.saveState = 'saving'
    } else if (newState === 'unsaved') {
      // Only update the unsaved state if there's no last save time
      if (!this.lastSaveTime) {
        this.saveState = 'unsaved'
      }
    }
  }

  getSaveStateUI() {
    if (this.saveState === 'saved' && this.lastSaveTime) {
      return `<span class="save-state-text saved just-saved">
                <span class="saved-checkmark">✔</span> ${Locale.getKey('lvc-footer-navigation.saved')}
                (${this.lastSaveTime})
              </span>`
    } else {
      return `<span class="save-state-text">
                <span class="not-saved-x"><lvc-blue-x-icon></lvc-blue-x-icon></span> ${Locale.getKey('lvc-footer-navigation.notSaved')}
              </span>`
    }
  }

  html = () => {
    return `
    ${
      parseInt(this.progress) >= 100
        ? ``
        : `
					<lvc-box class="footer-nav-container" color="#1E007D">
						<lvc-box display="flex" justifyContent="space-between" alignItems="center" height="12px" width="100%" maxWidth="1200px" margin="0 auto" class="progressBar">
							<div class="progress"></div>
						</lvc-box>
					<lvc-box display="flex" justifyContent="space-between" alignItems="center" height="100px" width="100%" padding="24px" maxWidth="1200px" margin="0 auto">
						<div class="left-menu">
							<lvc-simple-button label="${Locale.getKey('lvc-footer-navigation.quit')}"  id="buttonQuit" name="buttonQuit">
                ${Locale.getKey('lvc-footer-navigation.quit')}
                </lvc-simple-button>
                ${this.dataType === 'contribution' || this.dataType === 'contributor' ? '' : parseInt(this.progress) !== 0 || this.dataType === 'exhibition' ? this.getSaveStateUI() : ''}
                  
            </div>
            <lvc-box display="flex" alignItems="center" columnGap="60px">
         
              ${
                !this.steps.find(step => step.key === this.stepInfo?.stepName)
                  ? ''
                  : this.steps
                      .map(
                        (button, index) =>
                          `
                  <button
                    type="button"
                    class="navigation-button ${index < Object.values(this.steps).findIndex(step => step.key === this.stepInfo?.stepName) ? 'completed' : ''}"
                    data-key="${button.key}"
                    ${
                      button.key === this.stepInfo?.stepName ||
                      index <
                        Object.values(this.steps).findIndex(
                          step => step.key === this.stepInfo?.stepName
                        ) ||
                      (this.data?.[button.key] && Object.values(this.data?.[button.key]).length)
                        ? ''
                        : `disabled`
                    }
                  >
                    ${index + 1}<br/>
                    <span class="step-name">
                      <lvc-text font-weight="${Font.weight.semiBold}" font-size="10px" color="inherit">${button.label}</lvc-text>
                    </span>
                  </button>
                `
                      )
                      .join('')
              }
            </lvc-box>
            <lvc-box display="flex" rowGap="8px" alignItems="center" justifyContent="center">
              <lvc-simple-button label="${Locale.getKey('lvc-footer-navigation.return')}"  id="buttonReturn" type="button" name="buttonReturn">
              ${Locale.getKey('lvc-footer-navigation.return')}
              </lvc-simple-button>
                ${
                  ((this.dataType === 'event' || this.dataType === 'festival') &&
                    this.stepInfo?.stepName === 'finalEventRecap') ||
                  (this.dataType === 'exhibition' &&
                    this.stepInfo?.stepName === 'eventFirstRecap') ||
                  (this.dataType === 'contribution' &&
                    this.stepInfo?.stepName === 'createContributionRecap') ||
                  (this.dataType === 'contributor' &&
                    this.stepInfo?.stepName === 'createContributorRecap')
                    ? `
                <lvc-button disabled='${this.loading}' background="${this.loading ? '#6a54b1' : '#1E007D'}" enable-hover-box-shadow="true" margin-left="24px" width="150px" font-size="14px" label="${Locale.getKey('lvc-footer-navigation.commit')}"  id="buttonCommit" name="buttonCommit">${Locale.getKey('lvc-footer-navigation.commit')} </lvc-button>`
                    : `<lvc-button loading="${this.saveState === 'saving'}" enable-hover-box-shadow="true" background="#1E007D" type="button" label="${Locale.getKey('lvc-footer-navigation.next')}" font-size="14px" id="buttonNext" name="buttonNext" margin-left="24px" width="150px">
                ${Locale.getKey('lvc-footer-navigation.next')}
                </lvc-button>`
                }
            </lvc-box>

        </lvc-box>
        </lvc-box>
    `
    }`
  }

  css = () => {
    return /*css*/ `
      :host {
      }

      .footer-nav-container{
        background-color: #F5F5F5;
      }

      .left-menu {
        display: flex;
        align-items: center;
        gap: 24px;
      }

      .progressBar{
        background: var(--nuit-30, rgba(30, 0, 125, 0.30));
      }

      .just-saved {
        animation: savedFlash 1s ease-in-out;
      }

      @keyframes savedFlash {
        0% { opacity: 0.5; transform: scale(0.9); }
        50% { opacity: 1; transform: scale(1); }
        100% { opacity: 1; transform: scale(1); }
      }


      .save-state {
        font-size: 14px;
        font-weight: bold;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .saved {
        opacity: 0.5;
        transition: opacity 1s ease-in-out;
      }

      .save-state-text {
        display: flex;
        align-items: center;
        font-size: 12px;
        font-weight: 600;
        color: #1E007D;
        gap: 8px;
      }

      .saved-checkmark {
        color: #1E007D;
        font-size: 12px;
        font-weight: bold;
        animation: fadeIn 0.3s ease-in-out;
      }

      @keyframes fadeIn {
        from { opacity: 0; transform: scale(0.8); }
        to { opacity: 1; transform: scale(1); }
      }

      #buttonQuit, #buttonReturn{
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: 16.8px;
        color: var(--Minuit, #1E007D);
      }

      #buttonNext{
        display: flex;
        align-items: center;
      }

      .progress {
        width: ${this.progressPercentage || this.progress}%;
        height: 12px;
        background: var(--Minuit, #1E007D);
      }

      .navigation-button {
        width: 30px;
        height: 30px;
        background: white;
        border: 1px solid var(--Minuit, #1E007D);
        color: var(--Minuit, #1E007D);
        border-radius: 25px;
        position: relative;
        bottom: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: ${Font.weight.semiBold};
        cursor: pointer;
      }
      
      .navigation-button.completed::before {
        content: "✓";
        position: absolute;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        background: var(--Minuit, #1E007D);
        border-radius: 25px;
        color: #FFF;
      }
      
      .navigation-button.completed {
        background: var(--Minuit, #1E007D);
      }
      
      .navigation-button:disabled {
        color: #808080;
        border-color: #808080;
        pointer-events: none;
      }
      
      .navigation-button:not(:last-child)::after {
        content: "";
        width: 65px;
        height: 1.5px;
        background: var(--Minuit, #1E007D);
        position: absolute;
        right: -65px;
      }
      
      .navigation-button:disabled:not(:last-child)::after {
        background: #808080;
      }
      
      .navigation-button .step-name {
        position: absolute;
        bottom: -75%;
        white-space: nowrap;
      }
    `
  }

  eventListeners: EventListeners = [
    [this, 'blur', this.onBlur.bind(this)],
    ['div.overlay', 'click', this.onBlur.bind(this)],
    ['[name="buttonQuit"]', 'click', this.onClickButtonQuit.bind(this)],
    ['[name="buttonReturn"]', 'click', this.onClickButtonReturn.bind(this)],
    ['[name="buttonNext"]', 'click', this.onClickButtonNext.bind(this)],
    ['[name="buttonCommit"]', 'click', this.onClickButtonCommit.bind(this)],
    ['.navigation-button', 'click', this.onNavigationButtonClick.bind(this)],
    [window, 'state-confirmation-popup:left-btn-clicked', this.onClickPopupEdit.bind(this)],
    [window, 'state-confirmation-popup:right-btn-clicked', this.onClickPopupSubmit.bind(this)],
    [window, 'lvc:pro-space-save', this.onSaveStateChange.bind(this)],
    [window, 'lvc:flowcore-ready', this.onFlowcoreReady.bind(this)],
    [window, 'lvc:pro-space-draft-loaded', this.onDraftLoaded.bind(this)],
  ]
}
